<template>
	<div>
		<div v-if="loggedInEmployee.isAdmin">
			<v-snackbar v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
				{{ snackbar.message }}
				<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
			</v-snackbar>
			<loading-indicator v-if="featureFlagsLoading" loading-text="Loading features..." />
			<v-container grid-list-xl fluid v-else-if="featureFlagsLoaded" class="features-container">
				<v-layout row wrap>
					<v-flex md12 lg8 xl6>
						<v-expansion-panel expand :value="expansionPanel">
							<v-expansion-panel-content v-if="$_hasAccessToModule('compliance')">
								<template v-slot:header>
									<div>Compliance</div>
								</template>
								<v-list dense class="features-list">
									<v-list-tile>
										<v-list-tile-content>
											<v-list-tile-title>HR Policies</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<span class="enabled-text">On</span>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px"> </v-list-tile-action>
									</v-list-tile>
									<v-list-tile class="with-description">
										<v-list-tile-content>
											<v-list-tile-title>Compliance Reminders</v-list-tile-title>
											<v-list-tile-sub-title class="pr-4">Send out reminders to {{ $term('employee', { plural: true }) }} who have outstanding compliance items</v-list-tile-sub-title>
											<v-list-tile-sub-title class="pr-4 caption font-italic pt-2">
												<router-link :to="{ name: 'report', params: { name: 'EmployeeComplianceRemindersSentReport' } }"><v-icon class="mr-1" small>mdi mdi-finance</v-icon>View report of reminders sent</router-link>
											</v-list-tile-sub-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<v-switch @click.stop.prevent="onSetEmployeeComplianceReminders" :color="employeeComplianceReminders ? 'success' : ''" v-model="employeeComplianceReminders" :label="employeeComplianceReminders == true ? 'On' : 'Off'"> </v-switch>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px">
											<a href="#" @click.prevent="showComplianceRemindersHelpDialog = true">Learn More</a>
										</v-list-tile-action>
									</v-list-tile>
								</v-list>
							</v-expansion-panel-content>
							<v-expansion-panel-content v-if="$_hasAccessToModule('positionDescriptions')">
								<template v-slot:header>
									<div>{{ $term('positionDescription', { plural: true, case: 'title' }) }}</div>
								</template>
								<v-list dense class="features-list">
									<v-list-tile>
										<v-list-tile-content>
											<v-list-tile-title>Approval Workflow</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<span class="enabled-text">On</span>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px"> </v-list-tile-action>
									</v-list-tile>
									<v-list-tile class="with-description">
										<v-list-tile-content>
											<v-list-tile-title>{{ $term('employee', { case: 'title' }) }} acknowledgement required</v-list-tile-title>
											<v-list-tile-sub-title class="pr-4">Require {{ $term('employee', { plural: true }) }} to acknowledge an approved {{ $term('positionDescription', { case: 'sentence' }) }} before it becomes live.</v-list-tile-sub-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<v-switch
												:input-value="requireEmployeeAcknowledgementForPositionDescriptions"
												:disabled="isSavingPositionDescriptionSetting"
												:loading="isSavingPositionDescriptionSetting"
												:color="requireEmployeeAcknowledgementForPositionDescriptions ? 'success' : ''"
												:label="requireEmployeeAcknowledgementForPositionDescriptions ? 'On' : 'Off'"
												@click.stop.prevent="onSetEmployeeAcknowledgementRequired"
											></v-switch>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px"> </v-list-tile-action>
									</v-list-tile>
									<v-list-tile>
										<v-list-tile-content>
											<v-list-tile-title>{{ $term('positionTemplate', { plural: true, case: 'title' }) }}</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<span class="enabled-text">On</span>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px">
											<router-link :to="{ name: 'positionTemplates' }">Configure</router-link>
										</v-list-tile-action>
									</v-list-tile>
									<v-list-tile>
										<v-list-tile-content>
											<v-list-tile-title>Position Categories</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<span class="enabled-text">On</span>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px">
											<router-link :to="{ name: 'categories' }">Configure</router-link>
										</v-list-tile-action>
									</v-list-tile>
								</v-list>
							</v-expansion-panel-content>
							<v-expansion-panel-content  v-if="$_hasAccessToModule('performanceReviews')">
								<template v-slot:header>
									<div>Performance Reviews</div>
								</template>
								<v-list dense class="features-list">
									<v-list-tile>
										<v-list-tile-content>
											<v-list-tile-title>Performance Reviews</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<span class="enabled-text">On</span>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px"> </v-list-tile-action>
									</v-list-tile>

									<v-list-tile class="with-description">
										<v-list-tile-content>
											<v-list-tile-title>Condensed Performance Reviews</v-list-tile-title>
											<v-list-tile-sub-title class="pr-4">Option for a streamlined version to reduce the evaluation of key duties and deliverables in the {{ $term('positionDescription') }} (review is conducted using high-level categories only)</v-list-tile-sub-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<v-switch :color="condensedPerformanceReviews ? 'success' : ''" v-model="condensedPerformanceReviews" :label="condensedPerformanceReviews == true ? 'On' : 'Off'"> </v-switch>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px">
											
										</v-list-tile-action>
									</v-list-tile>

									<v-list-tile class="with-description">
										<v-list-tile-content>
											<v-list-tile-title>Reviewer-only completion</v-list-tile-title>
											<v-list-tile-sub-title class="pr-4">Allow reviewers to complete and sign off performance reviews without {{ $term('employee', { article: true }) }} submission.</v-list-tile-sub-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<v-switch
												v-model="allowReviewerCompletionWithoutEmployeeSubmission"
												:disabled="isSavingReviewerCompletionSetting"
												:loading="isSavingReviewerCompletionSetting"
												:color="allowReviewerCompletionWithoutEmployeeSubmission ? 'success' : ''"
												:label="allowReviewerCompletionWithoutEmployeeSubmission ? 'On' : 'Off'"
												@change="saveReviewerCompletionSetting"
											></v-switch>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px"> </v-list-tile-action>
									</v-list-tile>
									
									<v-list-tile>
										<v-list-tile-content>
											<v-list-tile-title>Custom Templates</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<span class="enabled-text">On</span>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px">
											<router-link :to="{ name: 'prSettings' }">Configure</router-link>
										</v-list-tile-action>
									</v-list-tile>
								</v-list>
							</v-expansion-panel-content>
							<v-expansion-panel-content  v-if="$_hasAccessToModule('checkIns')">
								<template v-slot:header>
									<div>Check Ins</div>
								</template>
								<v-list dense class="features-list">
									<v-list-tile>
										<v-list-tile-content>
											<v-list-tile-title>Check Ins</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<span class="enabled-text">On</span>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px"> </v-list-tile-action>
									</v-list-tile>
								</v-list>
							</v-expansion-panel-content>
							<v-expansion-panel-content v-if="$_hasAccessToModule('goals')">
								<template v-slot:header>
									<div>Goals</div>
								</template>
								<v-list dense class="features-list">
									<v-list-tile>
										<v-list-tile-content>
											<v-list-tile-title>{{ $term('employee', { case: 'title' }) }} Goals</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<span class="enabled-text">On</span>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px"> </v-list-tile-action>
									</v-list-tile>
									<v-list-tile>
										<v-list-tile-content>
											<v-list-tile-title>Goal Reminders</v-list-tile-title>
										</v-list-tile-content>
										<v-list-tile-action>
											<span class="enabled-text">On</span>
										</v-list-tile-action>
										<v-list-tile-action style="min-width: 100px"> </v-list-tile-action>
									</v-list-tile>
								</v-list>
							</v-expansion-panel-content>
						</v-expansion-panel>
					</v-flex>
				</v-layout>
			</v-container>
			<employee-compliance-reminder-help-dialog :dialog="showComplianceRemindersHelpDialog" @onClose="showComplianceRemindersHelpDialog = false" />
			
			<confirmation-dialog key="confirm-enable-compliance-reminders-dialog"
				v-if="showConfirmEnableComplianceRemindersDialog" 
				:dialog="showConfirmEnableComplianceRemindersDialog" 
				:isConfirming="isEnablingComplianceReminders"
				title="Confirm Enable Feature"
				@onConfirm="enableComplianceReminders" 
				@onCancel="showConfirmEnableComplianceRemindersDialog = false">

				<div class="py-2">Are you sure you want to enable Compliance Reminders?</div>
				
                <v-divider class="mt-2" />
              <div class="caption mt-3 mb-3" style="padding-left: 16px; color: rgba(0,0,0,.54);">Note: {{ $term('employee', { plural: true, case: 'sentence' }) }} will receieve a maximum of 1 reminder email per week. HR policies or {{ $term('positionDescription', { plural: true }) }} that were created or updated within the past week will not be included. Reminders will only be sent out on weekdays during typical working hours. It may take up to 1 hour before any reminders are sent out.</div>

			</confirmation-dialog>

			<confirmation-dialog key="confirm-disable-compliance-reminders-dialog"
				v-if="showConfirmDisableComplianceRemindersDialog" 
				:dialog="showConfirmDisableComplianceRemindersDialog" 
				:isConfirming="isDisablingComplianceReminders"
				title="Confirm Disable Feature"
				@onConfirm="disableComplianceReminders" 
				@onCancel="showConfirmDisableComplianceRemindersDialog = false">
				<div class="py-2">Are you sure you want to disable Compliance Reminders?<br><br><v-icon class="mr-1" small color="warning">info_outline</v-icon><span class="caption">This will stop reminders from being sent out to {{ $term('employee', { plural: true }) }} with immediate effect.</span></div>
			</confirmation-dialog>

			<confirmation-dialog
				key="confirm-disable-position-description-acknowledgement-dialog"
				v-if="showConfirmDisablePositionDescriptionAcknowledgementDialog"
				:dialog="showConfirmDisablePositionDescriptionAcknowledgementDialog"
				:isConfirming="isSavingPositionDescriptionSetting"
				:title="`Disable ${$term('employee', { case: 'title' })} Acknowledgement`"
				@onConfirm="disablePositionDescriptionAcknowledgement"
				@onCancel="showConfirmDisablePositionDescriptionAcknowledgementDialog = false">
				<div class="py-2 text-xs-left">
					Approved {{ $term('positionDescription', { plural: true, case: 'sentence' }) }} currently awaiting acknowledgement will become live immediately.
					No {{ $term('employee') }} acknowledgement will be recorded, and {{ $term('employee', { plural: true }) }} will not be asked to acknowledge those versions.
				</div>
			</confirmation-dialog>

		</div>
		<div v-else>You do not have access</div>
	</div>
</template>

<script>
	import { mapState, mapGetters } from 'vuex';
	import FeatureFlag from '@/enums/featureFlag';
	import LoadingIndicator from '@/components/common/LoadingIndicator';
	import EmployeeComplianceReminderHelpDialog from '@/components/compliance/EmployeeComplianceReminderHelpDialog';

	export default {
		components: { LoadingIndicator, EmployeeComplianceReminderHelpDialog },
		data() {
			return {
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink',
					timeout: 6000
				},
				expansionPanel: [true, false, true, false, false],
				showComplianceRemindersHelpDialog: false,
				showConfirmEnableComplianceRemindersDialog: false,
				showConfirmDisableComplianceRemindersDialog: false,
				isEnablingComplianceReminders: false,
				isDisablingComplianceReminders: false,

				showCondensedPerformanceReviewsHelpDialog: false,
				requireEmployeeAcknowledgementForPositionDescriptions: true,
				isSavingPositionDescriptionSetting: false,
				showConfirmDisablePositionDescriptionAcknowledgementDialog: false,
				allowReviewerCompletionWithoutEmployeeSubmission: false,
				isSavingReviewerCompletionSetting: false,

			};
		},
		created() {
			this.$store.dispatch('setPageTitle', 'Manage Features');
		},
		computed: {
			...mapState({
				tenantId: (state) => state.tenantId,
				featureFlagsLoading: (state) => state.features.featureFlagsLoading,
				featureFlagsLoaded: (state) => state.features.featureFlagsLoaded,
				featureFlags: (state) => state.features.featureFlags
			}),
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				loggedInTenant: 'loggedInTenant'
			}),
			employeeComplianceReminders: {
				get() {
					return this.featureFlags[FeatureFlag.EmployeeComplianceReminders];
				},
				set(newValue) {
					if (newValue) this.showConfirmEnableComplianceRemindersDialog = true;
					else this.showConfirmDisableComplianceRemindersDialog = true;
				}
			},
			condensedPerformanceReviews: {
				get() {
					return this.featureFlags[FeatureFlag.CondensedPerformanceReviews];
				},
				set(newValue) {
					const friendlyFeatureName = 'Condensed Performance Reviews';
					if (newValue) this.enableFeature(FeatureFlag.CondensedPerformanceReviews, friendlyFeatureName)
					else this.disableFeature(FeatureFlag.CondensedPerformanceReviews, friendlyFeatureName)
				}
			}
		},
		watch: {
			loggedInTenant: {
				immediate: true,
				handler(tenant) {
					this.requireEmployeeAcknowledgementForPositionDescriptions = !(
						tenant &&
						tenant.tenantSettings &&
						tenant.tenantSettings.requireEmployeeAcknowledgementForPositionDescriptions === false
					);

					if (!this.isSavingReviewerCompletionSetting) {
						this.allowReviewerCompletionWithoutEmployeeSubmission = !!(
							tenant &&
							tenant.tenantSettings &&
							tenant.tenantSettings.allowReviewerCompletionWithoutEmployeeSubmission
						);
					}
				}
			}
		},
		methods: {
			enableFeature(featureName, friendlyName) {
				return this.$store.dispatch('features/enableFeature', featureName).then(
					(response) => {
						this.showSnackbar(`Feature '${friendlyName}' has been enabled`);
					},
					(error) => {
						this.showSnackbar(`Failed to enable feature '${friendlyName}'`, true);
					}
				);
			},
			disableFeature(featureName, friendlyName) {
				return this.$store.dispatch('features/disableFeature', featureName).then(
					(response) => {
						this.showSnackbar(`Feature '${friendlyName}' has been disabled`);
					},
					(error) => {
						this.showSnackbar(`Failed to disable feature '${friendlyName}'`, true);
					}
				);
			},
			onSetEmployeeComplianceReminders() {
				if (this.employeeComplianceReminders) this.showConfirmDisableComplianceRemindersDialog = true;
				else this.showConfirmEnableComplianceRemindersDialog = true;
			},
			enableComplianceReminders() {
				this.isEnablingComplianceReminders = true;
				this.enableFeature(FeatureFlag.EmployeeComplianceReminders, 'Compliance Reminders')
				.finally(() => {
					this.isEnablingComplianceReminders = false;
					this.showConfirmEnableComplianceRemindersDialog = false;
				});
			},
			disableComplianceReminders() {
				this.isDisablingComplianceReminders = true;
				this.disableFeature(FeatureFlag.EmployeeComplianceReminders, 'Compliance Reminders')
				.finally(() => {
					this.isDisablingComplianceReminders = false;
					this.showConfirmDisableComplianceRemindersDialog = false;
				});
			},
			onSetEmployeeAcknowledgementRequired() {
				if (this.isSavingPositionDescriptionSetting) return;

				if (this.requireEmployeeAcknowledgementForPositionDescriptions) {
					this.showConfirmDisablePositionDescriptionAcknowledgementDialog = true;
				} else {
					this.savePositionDescriptionAcknowledgementSetting(true);
				}
			},
			disablePositionDescriptionAcknowledgement() {
				return this.savePositionDescriptionAcknowledgementSetting(false)
					.finally(() => {
						this.showConfirmDisablePositionDescriptionAcknowledgementDialog = false;
					});
			},
			async savePositionDescriptionAcknowledgementSetting(value) {
				const previousValue = this.requireEmployeeAcknowledgementForPositionDescriptions;
				const savingTenantId = this.tenantId;
				this.isSavingPositionDescriptionSetting = true;

				try {
					const settings = await this.$store.dispatch('employees/updatePositionDescriptionSettings', {
						requireEmployeeAcknowledgementForPositionDescriptions: value
					});

					if (this.tenantId === savingTenantId) {
						this.requireEmployeeAcknowledgementForPositionDescriptions =
							settings.requireEmployeeAcknowledgementForPositionDescriptions !== false;

						const madeLive = settings.positionDescriptionsMadeLive || 0;
						const positionDescription = this.$term('positionDescription', { count: madeLive });
						const message = madeLive > 0
							? `${this.$term('positionDescription', { case: 'title' })} setting saved. ${madeLive} ${positionDescription} ${madeLive === 1 ? 'is' : 'are'} now live.`
							: `${this.$term('positionDescription', { case: 'title' })} setting saved.`;
						this.showSnackbar(message);
					}
				} catch (error) {
					if (this.tenantId === savingTenantId) {
						this.requireEmployeeAcknowledgementForPositionDescriptions = previousValue;
						this.showSnackbar(`Failed to save the ${this.$term('positionDescription', { case: 'title' })} setting.`, true);
					}
				} finally {
					this.isSavingPositionDescriptionSetting = false;
				}
			},
			async saveReviewerCompletionSetting(value) {
				const previousValue = !value;
				this.isSavingReviewerCompletionSetting = true;

				try {
					const settings = await this.$store.dispatch('performanceReviews/updateSettings', {
						allowReviewerCompletionWithoutEmployeeSubmission: value
					});
					this.allowReviewerCompletionWithoutEmployeeSubmission = !!settings.allowReviewerCompletionWithoutEmployeeSubmission;
					this.showSnackbar('Performance Review setting saved.');
				} catch (error) {
					this.allowReviewerCompletionWithoutEmployeeSubmission = previousValue;
					this.showSnackbar('Failed to save the Performance Review setting.', true);
				} finally {
					this.isSavingReviewerCompletionSetting = false;
				}
			},
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			}
		}
	};
</script>

<style scoped>
	.enabled-text {
		color: #4caf50;
	}
	.features-container >>> .v-list > div.with-description .v-list__tile {
		height: initial !important;
		min-height: 72px;
	}
	.features-container >>> .v-list .v-list__tile .v-list__tile__title {
		height: initial !important;
		min-height: 24px !important;
		white-space: initial !important;
		font-weight: bold;
	}
	.features-container >>> .v-list .v-list__tile .v-list__tile__sub-title {
		white-space: initial !important;
	}
	.features-container >>> .group-heading {
		background-color: #eee;
	}
	.features-container >>> .v-expansion-panel__container {
		background-color: #eee;
	}
	.features-container .features-list {
		padding-left: 20px;
		padding-top: 10px;
		padding-bottom: 10px;
	}
</style>
